"use client";

import { useEffect, useRef, type ReactNode } from "react";

export function PresentationEffect({ effect, children }: { effect: unknown; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const mode = effect === "reveal" || effect === "parallax" ? effect : "none";
  useEffect(() => {
    const element = ref.current;
    if (!element || mode === "none") return;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const desktop = matchMedia("(min-width: 768px) and (pointer: fine)");
    let animation: Animation | undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      if (mode !== "parallax" || reduced.matches || !desktop.matches) {
        element.style.transform = "";
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > innerHeight) return;
      const distance = Math.max(-18, Math.min(18, (innerHeight / 2 - rect.top - rect.height / 2) * 0.035));
      element.style.transform = `translateY(${distance}px)`;
    };
    const scroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    const reset = () => { animation?.cancel(); element.style.transform = ""; scroll(); };
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      if (mode === "reveal" && !reduced.matches) {
        animation = element.animate([{ opacity: 0.65, transform: "translateY(12px)" }, { opacity: 1, transform: "none" }],
          { duration: 450, easing: "ease-out" });
      }
      observer.disconnect();
    }, { threshold: 0.05 });
    if (mode === "reveal") observer.observe(element);
    if (mode === "parallax") {
      addEventListener("scroll", scroll, { passive: true });
      addEventListener("resize", scroll);
      scroll();
    }
    reduced.addEventListener("change", reset);
    desktop.addEventListener("change", reset);
    return () => {
      observer.disconnect(); animation?.cancel(); cancelAnimationFrame(frame);
      removeEventListener("scroll", scroll); removeEventListener("resize", scroll);
      reduced.removeEventListener("change", reset); desktop.removeEventListener("change", reset);
      element.style.transform = "";
    };
  }, [mode]);
  if (mode === "none") return <>{children}</>;
  return <div ref={ref} data-presentation-effect={mode}>{children}</div>;
}
